import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Typography, withStyles, IconButton, Modal, Backdrop } from '@material-ui/core';
import { PictureViewStyles } from './styles';
import CloseIcon from '@material-ui/icons/Close';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import Moment from 'moment';
import PropTypes from 'prop-types';

const PictureView = ({ classes, treatment, onCloseImage, changeTreatment, applyMarginToImage }) => {
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [visibleImgSrc, setVisibleImgSrc] = useState(null);
    const [nextImgSrc, setNextImgSrc] = useState(null);
    const [isNextImgReady, setIsNextImgReady] = useState(false);
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const preloadedImages = useRef({});
    const treatmentId = treatment?.id;

    useEffect(() => {
        setCurrentPageIndex(0);
        setVisibleImgSrc(null);
        setNextImgSrc(null);
        setIsNextImgReady(false);
        setIsLoadingPage(false);
        preloadedImages.current = {};
    }, [treatmentId]);

    const { totalPages, getPageImage } = useMemo(() => {
        const canvasImagePerPage = treatment?.canvasImagePerPage;
        const canvasImageUrlOtherPages = treatment?.canvasImageUrlOtherPages;
        const canvasImageOtherPages = treatment?.canvasImageOtherPages;
        const mainImage = treatment?.lhrCanvasImage || treatment?.canvasImage;
        const modifiedAt = treatment?.modifiedAt;

        const addCacheBuster = url => {
            if (!url) return url;
            const separator = url.includes('?') ? '&' : '?';
            return modifiedAt ? `${url}${separator}t=${modifiedAt}` : url;
        };

        if (canvasImagePerPage && typeof canvasImagePerPage === 'object') {
            const pageKeys = Object.keys(canvasImagePerPage)
                .map(Number)
                .sort((a, b) => a - b);
            if (pageKeys.length > 1) {
                return {
                    totalPages: pageKeys.length,
                    getPageImage: pageIndex => addCacheBuster(canvasImagePerPage[pageKeys[pageIndex]])
                };
            }
        }

        const otherPages = canvasImageUrlOtherPages || canvasImageOtherPages;
        if (otherPages && Array.isArray(otherPages) && otherPages.some(url => url)) {
            const validUrls = otherPages.filter(url => url);
            return {
                totalPages: validUrls.length + 1,
                getPageImage: pageIndex => {
                    if (pageIndex === 0) return addCacheBuster(mainImage);
                    return addCacheBuster(validUrls[pageIndex - 1]);
                }
            };
        }

        return {
            totalPages: 1,
            getPageImage: () => addCacheBuster(mainImage)
        };
    }, [treatment]);

    useEffect(() => {
        for (let i = 0; i < totalPages; i++) {
            const url = getPageImage(i);
            if (url && !preloadedImages.current[url]) {
                const img = new Image();
                img.src = url;
                img.onload = () => {
                    preloadedImages.current[url] = true;
                };
            }
        }
    }, [totalPages, getPageImage]);

    useEffect(() => {
        const url = getPageImage(0);
        if (url && !visibleImgSrc) {
            setVisibleImgSrc(url);
        }
    }, [getPageImage, visibleImgSrc]);

    useEffect(() => {
        if (isNextImgReady && nextImgSrc) {
            setVisibleImgSrc(nextImgSrc);
            setNextImgSrc(null);
            setIsNextImgReady(false);
            setIsLoadingPage(false);
        }
    }, [isNextImgReady, nextImgSrc]);

    const goToPage = pageIndex => {
        const url = getPageImage(pageIndex);
        if (!url) return;

        setIsLoadingPage(true);
        setCurrentPageIndex(pageIndex);
        setNextImgSrc(url);
        setIsNextImgReady(false);
    };

    const handleNextImgLoad = () => {
        setIsNextImgReady(true);
    };

    return (
        <div>
            <div className={classes.header}>
                <IconButton onClick={() => onCloseImage(treatment.id)}>
                    <CloseIcon />
                </IconButton>
            </div>
            <div className={classes.imageView} style={applyMarginToImage ? { marginTop: 52 } : {}}>
                <div className={classes.imageArea} style={{ position: 'relative' }}>
                    {visibleImgSrc && (
                        <img
                            alt="Treatment"
                            className={classes.canvasImage}
                            src={visibleImgSrc}
                            onClick={() => setIsFullscreen(true)}
                            style={{ cursor: 'pointer' }}
                        />
                    )}
                    {nextImgSrc && nextImgSrc !== visibleImgSrc && (
                        <img
                            alt="Treatment loading"
                            src={nextImgSrc}
                            onLoad={handleNextImgLoad}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                opacity: 0,
                                pointerEvents: 'none'
                            }}
                        />
                    )}
                </div>
            </div>
            <div className={classes.pageNavigation}>
                {totalPages > 1 && (
                    <>
                        <IconButton
                            onClick={() => goToPage(currentPageIndex - 1)}
                            disabled={currentPageIndex === 0 || isLoadingPage}
                            size="small"
                            style={{
                                backgroundColor: 'white',
                                color: 'black',
                                border: '1px solid #ddd',
                                padding: '8px'
                            }}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <Typography className={classes.defaultFont}>
                            Page {currentPageIndex + 1} of {totalPages}
                        </Typography>
                        <IconButton
                            onClick={() => goToPage(currentPageIndex + 1)}
                            disabled={currentPageIndex >= totalPages - 1 || isLoadingPage}
                            size="small"
                            style={{
                                backgroundColor: 'white',
                                color: 'black',
                                border: '1px solid #ddd',
                                padding: '8px'
                            }}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </>
                )}
                <IconButton
                    onClick={() => setIsFullscreen(true)}
                    title="Open fullscreen"
                    size="small"
                    style={{
                        marginLeft: totalPages > 1 ? '20px' : undefined,
                        backgroundColor: 'white',
                        color: 'black',
                        border: '1px solid #ddd',
                        padding: '8px'
                    }}
                >
                    <ZoomInIcon />
                </IconButton>
            </div>
            <div className={classes.spacedContainer}>
                <IconButton onClick={() => changeTreatment(treatment.id, true)}>
                    <NavigateBeforeIcon />
                </IconButton>
                <Typography className={classes.defaultFont}>
                    {treatment.createdAt && Moment(treatment.createdAt).format('DD/MM/YYYY HH:mm')}
                </Typography>
                <Typography className={classes.defaultFont}>{treatment.service?.name}</Typography>
                <Typography className={classes.defaultFont}>{treatment.practitioner?.displayName}</Typography>
                <IconButton onClick={() => changeTreatment(treatment.id)}>
                    <NavigateNextIcon />
                </IconButton>
            </div>
            <Modal
                open={isFullscreen}
                onClose={() => setIsFullscreen(false)}
                BackdropComponent={Backdrop}
                BackdropProps={{
                    style: { backgroundColor: 'rgba(0, 0, 0, 0.95)' }
                }}
                className={classes.fullscreenModal}
            >
                <div className={classes.fullscreenContainer} onClick={() => setIsFullscreen(false)}>
                    {totalPages > 1 && (
                        <div className={classes.fullscreenPageNavigation} onClick={e => e.stopPropagation()}>
                            <IconButton
                                onClick={() => goToPage(currentPageIndex - 1)}
                                disabled={currentPageIndex === 0 || isLoadingPage}
                                className={classes.fullscreenNavButton}
                                size="small"
                            >
                                <ChevronLeftIcon />
                            </IconButton>
                            <Typography className={classes.fullscreenPageText}>
                                {currentPageIndex + 1} / {totalPages}
                            </Typography>
                            <IconButton
                                onClick={() => goToPage(currentPageIndex + 1)}
                                disabled={currentPageIndex >= totalPages - 1 || isLoadingPage}
                                className={classes.fullscreenNavButton}
                                size="small"
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </div>
                    )}
                    <IconButton
                        className={classes.fullscreenCloseButton}
                        onClick={() => setIsFullscreen(false)}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                    {visibleImgSrc && (
                        <img
                            alt="Treatment fullscreen"
                            className={classes.fullscreenImage}
                            src={visibleImgSrc}
                            onClick={e => e.stopPropagation()}
                        />
                    )}
                </div>
            </Modal>
        </div>
    );
};

PictureView.propTypes = {
    classes: PropTypes.object.isRequired,
    treatment: PropTypes.object.isRequired,
    onCloseImage: PropTypes.func.isRequired,
    changeTreatment: PropTypes.func.isRequired,
    applyMarginToImage: PropTypes.bool
};

export default withStyles(PictureViewStyles)(PictureView);
